import React, { useEffect, useState } from 'react';
import UploadSignatureFile from './UploadSignatureFile';
import moment from 'moment';
import SignaturePadComponent from './SignaturePadComponent';
import styled from 'styled-components';
import { StandardButton } from './GlobalElements/StandardButton.js';
import { ErrorMessage } from './GlobalElements/Typography';
import { tokens } from '../../public/tokens.js';
import { fetchWithRetry } from '../../utils/fetchWithRetry';

const { colors, typography, globals } = tokens;


const SignatureContainer = styled.div`
  margin-bottom: ${globals['spacing-sm']};
`;

const SignatureHeader = styled.div`
  margin-top: ${globals['spacing-sm']};
  margin-bottom: 4px;
`;

const SignatureTypeContainer = styled.div`
  display: ${props => props.readOnly ? 'none' : 'flex'};
  gap: 8px;
  margin-bottom: 4px;
  padding: 4px 0;
`;

const SignatureTypeOption = styled.div`
  border-bottom: ${props => props.active ? `1px solid ${colors.states.info.default}` : '0'};
  color: ${props => {
    if (props.active) return colors.states.info.default;
    if (props.disabled) return colors.text.secondary;
    return colors.text.primary;
  }};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.disabled ? colors.text.secondary : colors.states.info.default};
  }
  
  i {
    margin-right: 4px;
  }
`;

const ButtonGroup = styled.div`
  display: ${props => props.visible ? 'flex' : 'none'};
  margin: ${globals['spacing-sm']} 0;
  flex-direction: column;
  gap: 8px;
  
  @media (min-width: 576px) {
    flex-direction: row;
  }
`;

const InfoContainer = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const InfoItem = styled.span`
  color: ${colors.text.primary};
  font-size: ${typography.fontSize['lg-14px']};
  
  i {
    margin-right: 4px;
  }
`;

const SignatureIpInfo = styled.span`
  margin-bottom : 0px;
  font-size: ${typography.fontSize['lg-14px']};
  font-weight: ${typography.fontWeights['semibold']};
`;

const DigitalSignatureComponent = ({
  setErrorWhileUpload, 
  errorWhileUpload = false, 
  isAssessmentReview = null, 
  signaturePadRef,
  fileInputRef,
  formik,
  readOnly,
  readOnlyData,
  setIsSig,
  ipAddress,
  setIpAddress,
  cDate,
  setCDate,
  isCancel,
  setSigType,
  from = null, 
  removeSignature, 
  loading,
  initialComp = 0
}) => {
  const [signatureType, setSignatureType] = useState(from == 'quiz' && !readOnly ? initialComp : readOnly ? 1 : 0);
  const [fetchDateNIp, setFetchDateNIp] = useState(false);

  useEffect(() => {
    if(setSigType){
      setSigType(signatureType)
    }
  },[signatureType])

  useEffect(()=>{
    if(typeof setErrorWhileUpload === 'function' && errorWhileUpload){
      setFetchDateNIp(false)
      setErrorWhileUpload(false)
    }
  },[errorWhileUpload])


  useEffect(() => {
    if (!fetchDateNIp || readOnly) {
      return;
    }

    const controller = new AbortController();

    fetchWithRetry(
      'https://api.ipify.org?format=json',
      (data) => {
        if (data?.ip) {
          setIpAddress(data.ip);
          formik && formik.setFieldValue('ip', data.ip);
        }
      },
      { signal: controller.signal }
    );

    const now = new Date();
    setCDate(moment(now).format('DD MMM YYYY, HH:mm'));
    if (formik) {
      formik.setFieldValue('signature_date', now.getTime());
    }

    return () => controller.abort();
  }, [fetchDateNIp, readOnly]);

  useEffect(() => {
    setSignatureType(from == 'quiz' && !readOnly ? initialComp : readOnly ? 1 : 0)
  }, [readOnly, from, initialComp])

  const isTypeOptionDisabled = fetchDateNIp || readOnly;
  const isSignatureType0 = signatureType === 0;
  const isSignatureType1 = signatureType === 1;
  const showButtonContainer = !isAssessmentReview && readOnly && from === 'quiz' && readOnlyData?.signature_type === 0;

  return (
    readOnly && (!isAssessmentReview && (from == 'quiz' ? !readOnlyData?.signature : !readOnlyData?.student_signature)) ? null :
    <SignatureContainer>
      <SignatureHeader>Signature*</SignatureHeader>
      
      <SignatureTypeContainer readOnly={readOnly}>
        <SignatureTypeOption 
          active={isSignatureType0}
          disabled={isTypeOptionDisabled}
          onClick={() => { !isTypeOptionDisabled && setSignatureType(0) }}
        >
          <i className='fal fa-signature'></i>
          <span>Draw</span>
        </SignatureTypeOption>
        
        <SignatureTypeOption 
          active={isSignatureType1}
          disabled={isTypeOptionDisabled}
          onClick={() => { !isTypeOptionDisabled && setSignatureType(1) }}
        >
          <i className='fal fa-image'></i>
          <span>Upload Image</span>
        </SignatureTypeOption>
      </SignatureTypeContainer>
      
      {isSignatureType0 && (
        <>
          <SignaturePadComponent 
            setIsSig={setIsSig} 
            isCancel={isCancel} 
            readOnly={readOnly} 
            setShowIp={setFetchDateNIp} 
            formik={formik} 
            showIp={fetchDateNIp} 
            signaturePadRef={signaturePadRef} 
            from={from} 
            loading={loading} 
            readOnlyData={readOnlyData}
          />
        </>
      )}
      
      {isSignatureType1 && 
        <UploadSignatureFile 
          setIsSig={setIsSig} 
          isCancel={isCancel} 
          readOnly={readOnly} 
          readOnlyData={readOnlyData} 
          setShowIp={setFetchDateNIp} 
          formik={formik} 
          showIp={fetchDateNIp} 
          fileInputRef={fileInputRef} 
          from={from} 
          removeSignature={removeSignature} 
          isAssessmentReview={isAssessmentReview}
        />
      }
      
      {showButtonContainer && (
        <ButtonGroup visible={showButtonContainer}>
          <StandardButton disabled={true}>Save Signature</StandardButton>
          <StandardButton variant='danger' onClick={() => { removeSignature(0); setFetchDateNIp(false) }}>
            Remove Signature
          </StandardButton>
        </ButtonGroup>
      )}
      
      <InfoContainer>
        <InfoItem>
          <i className='fal fa-info-circle'></i>
          <SignatureIpInfo>IP:</SignatureIpInfo> {readOnly ? (String(from) === 'quiz' ? readOnlyData?.signature_ip : readOnlyData?.ip) : (fetchDateNIp ? ipAddress : '')}
        </InfoItem>
        
        <InfoItem>
          <SignatureIpInfo>Date: </SignatureIpInfo>
          {readOnly ? (String(from) === 'quiz' ? readOnlyData?.signature_date : moment(parseInt(readOnlyData?.signature_date*1000))?.format('DD MMM YYYY, HH:mm')) : (fetchDateNIp ? cDate : '')}
        </InfoItem>
      </InfoContainer>
      
      {formik?.touched?.student_signature && formik?.errors?.student_signature ? (
        <ErrorMessage>
          {formik.errors.student_signature}
        </ErrorMessage>
      ) : null}
    </SignatureContainer>
  );
};

export default DigitalSignatureComponent;